import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

const FullWidthGradeCharts = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const [grades, setGrades] = useState([]);
  const [selectedClass, setSelectedClass] = useState('Advanced Biology F25');
  const [hoverIndex, setHoverIndex] = useState(null);

  const classes = [
    'Advanced Biology F25',
    'Adv Algebra 2 - Rose',
    'Schwarz: English II',
    'Spanish 1 - Fall 2025',
    'Adv Design Applications'
  ];

  const fetchGrades = async () => {
    try {
      const gradesSnapshot = await getDocs(collection(db, 'grades'));
      const gradesList = gradesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGrades(gradesList);
    } catch (e) {
      console.error('Error fetching grades', e);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchGrades();
    }
  }, [isAuthenticated]);

  const generateChartData = (className) => {
    const classGrades = grades
      .filter(g => g.className === className)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return classGrades.map(g => ({
      date: new Date(g.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      formative: g.formativePercent,
      summative: g.summativePercent,
      overall: g.overallPercent
    }));
  };

  const ChartWithHover = ({ data, series, height = 300 }) => {
    const containerRef = useRef(null);
    const [width, setWidth] = useState(800);

    useEffect(() => {
      const update = () => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) setWidth(Math.max(320, rect.width));
      };
      update();
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }, []);

    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center text-gray-500 text-sm" style={{ height: height / 2 }}>
          No data yet
        </div>
      );
    }

    const allValues = series
      .flatMap(s => data.map(d => d[s.key]))
      .filter(v => typeof v === 'number' && !isNaN(v));
    if (allValues.length === 0) {
      return (
        <div className="flex items-center justify-center text-gray-500 text-sm" style={{ height: height / 2 }}>
          No data yet
        </div>
      );
    }

    const rawMin = Math.min(...allValues);
    const rawMax = Math.max(...allValues);
    const span = Math.max(0.0001, rawMax - rawMin);
    const pad = Math.max(2, span * 0.1);
    let yMin = Math.max(0, rawMin - pad);
    let yMax = Math.min(100, rawMax + pad);
    const minRange = 20;
    if (yMax - yMin < minRange) {
      const need = minRange - (yMax - yMin);
      yMin = Math.max(0, yMin - need / 2);
      yMax = Math.min(100, yMax + need / 2);
    }
    yMin = Math.floor(yMin / 5) * 5;
    yMax = Math.ceil(yMax / 5) * 5;
    if (yMax <= yMin) yMax = Math.min(100, yMin + minRange);

    const chartWidth = width;
    const chartHeight = height;
    const padding = 12;
    const plotWidth = chartWidth - 2 * padding;
    const plotHeight = chartHeight - 2 * padding;

    const getX = (index) => {
      if (data.length === 1) return padding + plotWidth / 2;
      return (index / (data.length - 1)) * plotWidth + padding;
    };
    const getY = (value) => chartHeight - padding - ((value - yMin) / (yMax - yMin)) * plotHeight;

    const handleMouseMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left - padding;
      const clamped = Math.max(0, Math.min(plotWidth, x));
      const idx = data.length > 1 ? Math.round((clamped / plotWidth) * (data.length - 1)) : 0;
      setHoverIndex(idx);
    };
    const handleLeave = () => setHoverIndex(null);

    const hoverX = hoverIndex != null ? getX(hoverIndex) : null;
    const hoverPoint = hoverIndex != null ? data[hoverIndex] : null;

    return (
      <div ref={containerRef} className="relative w-full">
        <div className="relative flex items-start">
          <svg
            width={chartWidth}
            height={chartHeight}
            className="overflow-visible"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleLeave}
          >
            <defs>
              <pattern id="fw-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#4d4d4d" strokeWidth="0.5" opacity="0.2" />
              </pattern>
            </defs>
            <rect width={chartWidth} height={chartHeight} fill="url(#fw-grid)" />

            {series.map((s) => {
              const segments = [];
              let current = [];
              data.forEach((point, index) => {
                const v = point[s.key];
                if (typeof v === 'number' && !isNaN(v)) {
                  current.push({ index, value: v });
                } else if (current.length > 0) {
                  segments.push(current);
                  current = [];
                }
              });
              if (current.length > 0) segments.push(current);

              return (
                <g key={s.key}>
                  {segments.map((seg, idx) => (
                    <polyline
                      key={`${s.key}-seg-${idx}`}
                      points={seg.map(({ index, value }) => `${getX(index)},${getY(value)}`).join(' ')}
                      fill="none"
                      stroke={s.color}
                      strokeWidth="2.5"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      opacity="0.9"
                    />
                  ))}

                  {data.map((point, index) => {
                    const v = point[s.key];
                    if (typeof v !== 'number' || isNaN(v)) return null;
                    return (
                      <circle
                        key={`${s.key}-pt-${index}`}
                        cx={getX(index)}
                        cy={getY(v)}
                        r={hoverIndex === index ? 4.5 : 3.5}
                        fill={s.color}
                        stroke="#0d0d0d"
                        strokeWidth="1.5"
                        opacity="1"
                      />
                    );
                  })}
                </g>
              );
            })}

            {hoverX != null && (
              <g>
                <line x1={hoverX} y1={padding} x2={hoverX} y2={chartHeight - padding} stroke="#888" strokeDasharray="4 3" opacity="0.7" />
              </g>
            )}
          </svg>

          <div className="ml-4 flex flex-col space-y-1">
            {series.map(s => (
              <div key={s.key} className="flex items-center space-x-2 text-sm">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }}></div>
                <span style={{ color: s.color }}>{s.label}</span>
              </div>
            ))}
          </div>

          {hoverPoint && (
            <div
              className="absolute pointer-events-none bg-[#1a1a1a] border border-[#4d4d4d] rounded p-3 text-xs shadow"
              style={{
                left: Math.min(Math.max(hoverX + 8, 0), chartWidth - 180),
                top: 8
              }}
            >
              <div className="font-semibold mb-1">{hoverPoint.date}</div>
              {series.map(s => {
                const v = hoverPoint[s.key];
                const hasValue = typeof v === 'number' && !isNaN(v);
                return (
                  <div key={`tip-${s.key}`} className="flex items-center justify-between space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }}></div>
                      <span className="text-gray-300">{s.label}</span>
                    </div>
                    <span className="text-white">{hasValue ? `${v.toFixed(1)}%` : '--'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="absolute left-0 right-0 -bottom-6 flex justify-between text-xs text-gray-400">
          <span>{data[0]?.date}</span>
          <span>{data[data.length - 1]?.date}</span>
        </div>
      </div>
    );
  };

  const data = generateChartData(selectedClass);

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <AdminHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Full-Width Grade Charts</h1>
            <p className="text-gray-400">Interactive trends with hover details</p>
          </div>
          <Link
            to="/admin/progress-analytics"
            className="text-blue-400 hover:text-blue-300 text-sm"
          >
            ← Back to Analytics
          </Link>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {classes.map(className => (
            <button
              key={className}
              onClick={() => setSelectedClass(className)}
              className={`px-3 py-2 rounded text-sm ${selectedClass === className ? 'bg-blue-600' : 'bg-[#2a2a2a] hover:bg-[#3a3a3a]'} transition-colors`}
            >
              {className}
            </button>
          ))}
        </div>

        <div className="w-full bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-4 overflow-x-hidden">
          <div className="text-xs text-gray-400 mb-2">Grade Trends - {selectedClass}</div>
          <ChartWithHover
            data={data}
            series={[
              { key: 'formative', label: 'Formative', color: '#60a5fa' },
              { key: 'summative', label: 'Summative', color: '#f87171' },
              { key: 'overall', label: 'Overall', color: '#34d399' }
            ]}
            height={320}
          />
        </div>
      </div>
    </div>
  );
};

export default FullWidthGradeCharts;


